import React, { useEffect, useState } from 'react';
import { Alert, Modal, Table, Tag, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { getStatsRouteBreakdown } from '../modules/partners/api';

const fmtMins = (mins) => {
  if (mins == null) return '—';
  const h = Math.floor(Math.abs(mins) / 60);
  const m = Math.round(Math.abs(mins) % 60);
  return `${mins < 0 ? '-' : ''}${h}h ${String(m).padStart(2, '0')}m`;
};

/** Which per-route column a KPI decomposes into. */
export const DRILLDOWN_METRICS = {
  shift: { key: 'shiftMins', label: 'Shift', format: fmtMins },
  clientService: { key: 'clientServiceMins', label: 'Client service', format: fmtMins },
  distance: { key: 'distanceKm', label: 'Distance', format: (v) => (v == null ? '—' : `${v} km`) },
  stops: { key: 'stops', label: 'Stops', format: (v) => v ?? 0 },
  routes: { key: null, label: 'Routes', format: () => '' },
};

/**
 * OVO-248 — the routes that add up to a stats KPI.
 *
 * Felipe: *« tu cliques et tu vois toutes les durées de shift qui font la
 * somme »*. An aggregate he can't decompose is one he can't trust — Total Shift
 * Time was inflated by a route whose shift never closed and there was no way to
 * find it. Rows come from the same query, range and per-route maths as the card
 * itself, sorted worst-first, so the outlier is the first line.
 */
export default function StatsRoutesDrilldown({ open, metric, title, query, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getStatsRouteBreakdown(query)
      .then(({ response }) => {
        if (!cancelled) setData(response || null);
      })
      .catch((e) => !cancelled && setError(e?.message || 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(query)]);

  const meta = DRILLDOWN_METRICS[metric] || DRILLDOWN_METRICS.shift;
  const rows = data?.rows || [];
  const total = meta.key ? rows.reduce((a, r) => a + (r[meta.key] ?? 0), 0) : rows.length;

  const columns = [
    {
      title: 'Route',
      dataIndex: 'routeId',
      key: 'routeId',
      render: (id, rec) => (
        <span>
          <Link to={`/admin/routes/${id}`}>#{id}</Link>
          {/* Explain an outlier in place, so nobody has to open the route to
              understand why the total looks wrong. */}
          {rec.implausibleShift && (
            <Tooltip title="Shift longer than a working day — almost certainly never closed. Fix it from Monitoring → Inconsistencies.">
              <Tag color="red" style={{ marginLeft: 6 }}>
                unclosed?
              </Tag>
            </Tooltip>
          )}
          {rec.shiftCorrected && (
            <Tooltip title="Shift end was auto-corrected to the last validated stop (OVO-260).">
              <Tag color="blue" style={{ marginLeft: 6 }}>
                corrected
              </Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    { title: 'Date', dataIndex: 'date', key: 'date' },
    { title: 'Driver', dataIndex: 'driverName', key: 'driverName', render: (v) => v || '—' },
    { title: 'Vehicle', dataIndex: 'vehicle', key: 'vehicle', render: (v) => v || '—' },
  ];
  if (meta.key) {
    columns.push({
      title: meta.label,
      dataIndex: meta.key,
      key: meta.key,
      align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a[meta.key] ?? 0) - (b[meta.key] ?? 0),
      render: (v) => <strong>{meta.format(v)}</strong>,
    });
  }

  return (
    <Modal open={open} title={title} onCancel={onClose} footer={null} width={860} destroyOnClose>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          meta.key
            ? `${rows.length} route(s) · total ${meta.format(total)} — the same filters and period as the card.`
            : `${rows.length} route(s) — the same filters and period as the card.`
        }
      />
      <Table
        loading={loading}
        dataSource={rows}
        columns={columns}
        rowKey="routeId"
        size="small"
        pagination={{ pageSize: 15, showSizeChanger: false }}
        scroll={{ x: 'max-content', y: 420 }}
      />
    </Modal>
  );
}
